import {
  nr2jCreditNoteStatusBadge,
  nr2jInvoiceStatusBadge,
  nr2jQuoteStatusBadge,
} from './nr2jConstants.js';

export const NR2J_APPROVALS_CHANGED_EVENT = 'clickorico:nr2j-approvals-changed';

export const NR2J_APPROVAL_TYPE_FILTERS = [
  { id: '', label: 'Tous' },
  { id: 'QUOTE', label: 'Devis' },
  { id: 'INVOICE', label: 'Factures' },
  { id: 'CREDIT_NOTE', label: 'Avoirs' },
];

const TYPE_LABELS = {
  QUOTE: 'Devis',
  INVOICE: 'Facture',
  CREDIT_NOTE: 'Avoir',
};

export const NR2J_APPROVAL_DECISIONS = {
  APPROVED: { label: 'Valider', success: 'Document validé' },
  CHANGES_REQUESTED: { label: 'Demander des modifications', success: 'Modifications demandées' },
  REJECTED: { label: 'Refuser', success: 'Document refusé' },
};

export function nr2jApprovalTypeLabel(entityType) {
  return TYPE_LABELS[entityType] || entityType || '—';
}

export function nr2jApprovalDecisionNeedsComment(decision) {
  return decision === 'CHANGES_REQUESTED' || decision === 'REJECTED';
}

export function approvalUserName(user) {
  if (!user) return '—';
  const full = `${user.prenom || ''} ${user.nom || ''}`.trim();
  return full || user.email || '—';
}

export function approvalDocumentLabel(approval) {
  const doc = approval?.document;
  const typeLabel = nr2jApprovalTypeLabel(approval?.entityType);
  if (doc?.number) {
    return doc.version && approval.entityType === 'QUOTE'
      ? `${doc.number} · v${doc.version}`
      : doc.number;
  }
  return `${typeLabel} brouillon`;
}

export function approvalDocumentStatusBadge(approval) {
  const status = approval?.document?.status;
  if (approval?.entityType === 'QUOTE') return nr2jQuoteStatusBadge(status);
  if (approval?.entityType === 'INVOICE') return nr2jInvoiceStatusBadge(status);
  return nr2jCreditNoteStatusBadge(status);
}

export function approvalAmountCents(approval) {
  const value = approval?.document?.totalTtcCents;
  const n = Number(value);
  return value == null || !Number.isFinite(n) ? null : n;
}

export function summarizeApprovals(approvals) {
  const rows = approvals || [];
  const amounts = rows.map(approvalAmountCents).filter((n) => n != null);
  return {
    total: rows.length,
    quotes: rows.filter((row) => row.entityType === 'QUOTE').length,
    invoices: rows.filter((row) => row.entityType === 'INVOICE').length,
    creditNotes: rows.filter((row) => row.entityType === 'CREDIT_NOTE').length,
    amountCents: amounts.length ? amounts.reduce((sum, n) => sum + n, 0) : null,
  };
}

export function approvalCollaboratorOptions(approvals) {
  const byId = new Map();
  for (const row of approvals || []) {
    if (row.requestedByUserId && !byId.has(row.requestedByUserId)) {
      byId.set(row.requestedByUserId, approvalUserName(row.requestedBy));
    }
  }
  return [...byId.entries()]
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
}

export function approvalStatusOptions(approvals) {
  const byLabel = new Map();
  for (const row of approvals || []) {
    if (!row.document?.status) continue;
    const { label } = approvalDocumentStatusBadge(row);
    if (!byLabel.has(label)) byLabel.set(label, label);
  }
  return [...byLabel.keys()].sort((a, b) => a.localeCompare(b, 'fr'));
}

export function filterApprovals(approvals, { type = '', collaboratorId = '', statusLabel = '', q = '' } = {}) {
  const needle = q.trim().toLowerCase();
  return (approvals || []).filter((row) => {
    if (type && row.entityType !== type) return false;
    if (collaboratorId && row.requestedByUserId !== collaboratorId) return false;
    if (statusLabel && approvalDocumentStatusBadge(row).label !== statusLabel) return false;
    if (!needle) return true;
    const haystack = [
      approvalDocumentLabel(row),
      row.document?.organizationName,
      approvalUserName(row.requestedBy),
      row.requestedBy?.email,
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(needle);
  });
}
